import { IconButton } from '@material-ui/core';
import { IList } from 'annotation-tools/utils';
import React, { CSSProperties, memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { texts } from '../../../constants/texts';
import { IdType, ILabelType } from '../../../imports/annotation-canvas';
import {
  CollapseListIcon,
  ExpandListIcon,
  VisibilityAll
} from '../../../imports/ui-parts';
import { Percent, t } from '../../../imports/utils';
import { LabelButtonItem } from './label-button-item/label-button-item';

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  user-select: none;
  -webkit-touch-callout: none;
`;

const Title = styled.div`
  flex-grow: 0;
  padding: 10px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Content = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const LabelsWrapper = styled.div`
  overflow-y: auto;
`;

export const LabelButtons = memo(
  ({
    labels,
    labelSaturation,
    labelLightness,
    visibleLabels,
    onLabelClick,
    onVisibilityClick,
    onShowAllClick,
    onHideAllClick,
    selectedLabel,
    hidden,
    onHiddenChange,
    disabled
  }: Readonly<{
    labels: IList<ILabelType>;
    labelSaturation: Percent;
    labelLightness: Percent;
    visibleLabels: IList<ILabelType>;
    onLabelClick: (id: IdType) => void;
    onVisibilityClick: (id: IdType) => void;
    onShowAllClick: () => void;
    onHideAllClick: () => void;
    selectedLabel: ILabelType | undefined;
    hidden: boolean;
    onHiddenChange: (v: boolean) => void;
    disabled: boolean;
  }>) => {
    const displayClick = useCallback(() => {
      onHiddenChange(false);
    }, [onHiddenChange]);

    const hideClick = useCallback(() => {
      onHiddenChange(true);
    }, [onHiddenChange]);

    const style = useMemo<CSSProperties>(
      () =>
        hidden
          ? { overflowY: 'hidden', maxHeight: '0' }
          : { overflowY: 'auto' },
      [hidden]
    );

    const labelsWithFlags = useMemo(
      () =>
        labels.map(label =>
          t(
            label,
            visibleLabels.includes(label),
            label.id === selectedLabel?.id
          )
        ),
      [labels, visibleLabels, selectedLabel]
    );

    return (
      <Root>
        <Title>
          {texts.labelList}
          {hidden ? (
            <IconButton size='small' onClick={displayClick}>
              <ExpandListIcon fontSize='inherit' />
            </IconButton>
          ) : (
            <IconButton size='small' onClick={hideClick}>
              <CollapseListIcon fontSize='inherit' />
            </IconButton>
          )}
        </Title>

        <Content style={style}>
          <LabelsWrapper>
            {labelsWithFlags.map(([label, isVisible, isSelected], index) => (
              <LabelButtonItem
                index={index}
                key={label.id}
                label={label}
                labelSaturation={labelSaturation}
                labelLightness={labelLightness}
                isSelectedLabel={isSelected}
                isVisibleLabel={isVisible}
                onLabelClick={onLabelClick}
                onVisibilityClick={onVisibilityClick}
                disabled={disabled}
              />
            ))}
          </LabelsWrapper>
          <VisibilityAll
            numVisibleLabels={visibleLabels.size}
            numLabels={labels.size}
            onShowAllClick={onShowAllClick}
            onHideAllClick={onHideAllClick}
            disabled={disabled}
          />
        </Content>
      </Root>
    );
  }
);

LabelButtons.displayName = 'LabelButtons';
